import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { ROLES, formatConditionValue, getConditionTag, type ConditionTagCode } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Avatar, Button, Card, Chip, ErrorNote, SectionTitle, Spinner, Textarea } from '@/components/ui';
import { apiData } from '@/lib/api';
import { useMediaUrl } from '@/lib/media-url';
import { TranslateBlock } from '@/components/TranslateBlock';
import { SafetyMenu } from '@/components/SafetyActions';
import { formatDate, formatRelative } from '@/lib/format';
import { useAuth, useChildren, useRole } from '@/store/auth';
import type { DailyNote } from '@/types';

const REACTIONS = ['💚', '👏', '🥰', '😊'];

/** 알림장 상세 — 읽음 확인·이모지 반응·댓글·가정 소식 (§5.3) */
export function DailyNoteDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const role = useRole();
  const children = useChildren();
  const userId = useAuth((s) => s.user?.id);
  const isParent = role === ROLES.PARENT;

  const [comment, setComment] = useState('');
  const [homeMessage, setHomeMessage] = useState('');
  const [homeMessageSent, setHomeMessageSent] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['daily-notes', id],
    queryFn: () => apiData<DailyNote>(`/daily-notes/${id}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['daily-notes', id] });
    void queryClient.invalidateQueries({ queryKey: ['daily-notes'] });
  };

  const addComment = useMutation({
    mutationFn: (content: string) =>
      apiData(`/daily-notes/${id}/comments`, { method: 'POST', body: { content } }),
    onSuccess: () => {
      setComment('');
      invalidate();
    },
  });

  const toggleReaction = useMutation({
    mutationFn: (emoji: string) =>
      apiData(`/daily-notes/${id}/reactions`, { method: 'POST', body: { emoji } }),
    onSuccess: invalidate,
  });

  const sendHomeMessage = useMutation({
    mutationFn: (content: string) =>
      apiData('/daily-notes/home-messages', {
        method: 'POST',
        body: { childId: targetChildId, noteId: id, content },
      }),
    onSuccess: () => {
      setHomeMessage('');
      setHomeMessageSent(true);
    },
  });

  if (isPending) return <Spinner />;
  if (isError || !data) {
    return (
      <Screen withNav={false}>
        <AppBar title="알림장" back />
        <ErrorNote message="알림장을 불러오지 못했어요." onRetry={() => void refetch()} />
      </Screen>
    );
  }

  const targetChildId = data.child?.id ?? children[0]?.id ?? '';
  // 반 일괄 알림장이어도 컨디션은 원아별로 남는다 (§5.3).
  const myConditions = data.conditions.filter(
    (c) => !isParent || children.some((child) => child.id === c.childId),
  );

  return (
    <Screen withNav={false}>
      <AppBar
        title={data.title ?? '알림장'}
        subtitle={`${data.child?.name ?? data.classRoom?.name ?? ''} · ${formatDate(data.noteDate)}`}
        back
      />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <p className="whitespace-pre-wrap text-base leading-relaxed">{data.content}</p>
          {data.editedAt && (
            <p className="mt-3 text-sm text-ink-sub">{formatRelative(data.editedAt)} 수정되었어요</p>
          )}
          <TranslateBlock text={data.content} />
        </Card>

        {data.photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {data.photos.map((photo) => (
              <PhotoTile key={photo.id} mediaId={photo.media.id} />
            ))}
          </div>
        )}

        {myConditions.length > 0 && (
          <section>
            <SectionTitle>오늘의 컨디션</SectionTitle>
            <Card className="flex flex-wrap gap-2">
              {myConditions.map((condition) => {
                const tag = getConditionTag(condition.tagCode);
                const childName = children.find((c) => c.id === condition.childId)?.name;
                return (
                  <Chip key={condition.id} tone="primary">
                    {children.length > 1 && childName ? `${childName} · ` : ''}
                    {tag?.label ?? condition.tagCode}
                    {': '}
                    <b>{formatConditionValue(condition.tagCode as ConditionTagCode, condition.value)}</b>
                  </Chip>
                );
              })}
            </Card>
          </section>
        )}

        <section>
          <div className="flex flex-wrap gap-2">
            {REACTIONS.map((emoji) => {
              const mine = data.myReactions.includes(emoji);
              const count = data.reactionSummary[emoji] ?? 0;
              return (
                <Chip
                  key={emoji}
                  tone={mine ? 'secondary' : 'neutral'}
                  selected={mine}
                  onClick={() => toggleReaction.mutate(emoji)}
                >
                  <span className="text-base">{emoji}</span>
                  {count > 0 && <span className="tnum">{count}</span>}
                </Chip>
              );
            })}
          </div>
        </section>

        <section>
          <SectionTitle
            action={isParent ? <SafetyMenu target={{ type: 'DAILY_NOTE', id: data.id }} /> : undefined}
          >
            댓글 {data._count.comments > 0 && `(${data._count.comments})`}
          </SectionTitle>
          <div className="flex flex-col gap-3">
            {data.comments?.map((item) => (
              <Card key={item.id} className="flex gap-3 p-3">
                <Avatar name={item.user.name} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {item.user.name}
                    {item.user.id === userId && <span className="text-ink-sub"> (나)</span>}
                    <span className="ml-2 font-normal text-ink-sub">
                      {formatRelative(item.createdAt)}
                    </span>
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap text-base">{item.content}</p>
                </div>
                {/* 안전 도구 (§5.30) — 내 댓글에는 붙지 않는다 */}
                <SafetyMenu target={{ type: 'COMMENT', id: item.id }} user={item.user} className="-mr-2 -mt-1" />
              </Card>
            ))}

            <form
              onSubmit={(event: FormEvent) => {
                event.preventDefault();
                if (comment.trim()) addComment.mutate(comment.trim());
              }}
              className="flex gap-2"
            >
              <Textarea
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="따뜻한 한마디를 남겨 보세요"
                className="flex-1"
              />
              <Button
                type="submit"
                aria-label="댓글 보내기"
                loading={addComment.isPending}
                disabled={!comment.trim()}
                className="self-end"
                icon={<Send size={16} />}
              />
            </form>
          </div>
        </section>

        {/* 가정 소식 — 가정에서 기관으로 전하는 이야기 (§5.3) */}
        {isParent && targetChildId && (
          <section>
            <SectionTitle>가정에서 전하기</SectionTitle>
            <Card>
              {homeMessageSent ? (
                <p className="text-base text-ink">
                  선생님께 전달했어요. 확인하시면 알림장으로 답을 들려 드릴게요.
                </p>
              ) : (
                <form
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    if (homeMessage.trim()) sendHomeMessage.mutate(homeMessage.trim());
                  }}
                  className="flex flex-col gap-2"
                >
                  <Textarea
                    rows={3}
                    value={homeMessage}
                    onChange={(e) => setHomeMessage(e.target.value)}
                    placeholder="등원 전 전달하고 싶은 이야기를 적어 주세요"
                  />
                  <Button
                    type="submit"
                    variant="secondary"
                    loading={sendHomeMessage.isPending}
                    disabled={!homeMessage.trim()}
                  >
                    선생님께 전하기
                  </Button>
                </form>
              )}
            </Card>
          </section>
        )}
      </div>
    </Screen>
  );
}

/** 사진은 서명 URL로만 접근한다 (§9.1) */
function PhotoTile({ mediaId }: { mediaId: string }) {
  const { data } = useMediaUrl(mediaId);

  return (
    <div className="aspect-square overflow-hidden rounded-sm bg-primary-soft">
      {data?.url && <img src={data.url} alt="" className="h-full w-full object-cover" />}
    </div>
  );
}
